/**
 * The concept-name pill the Knowledge Archive canvas paints under a node, and
 * the phone's resting-name pass.
 *
 * Everything is in GRAPH units: the canvas is drawn under the camera's
 * transform, so a pill that should be 11px on screen is `11 / k` here.
 */

import {
  LABEL_FONT_PX,
  labelFont,
  truncateLabel,
} from '@/lib/mindspace/graphViewport';

export interface LabelColours {
  pill: string;
  text: string;
}

interface LabelledNode {
  x?: number;
  y?: number;
  name: string;
  size?: number;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Where a node's pill goes: centred under its disc, at scale `k`. */
function pillFor(
  ctx: CanvasRenderingContext2D,
  node: LabelledNode,
  k: number
): { text: string; box: Box } {
  const fontSize = LABEL_FONT_PX / k;
  // Canvas takes a font string, not a class, so the family is named
  // explicitly (`labelFont`). It must match the self-hosted family actually
  // loaded ("Inter Variable") - naming plain "Inter" silently fell through to
  // system-ui once the Google Fonts copy was removed.
  ctx.font = labelFont(fontSize);
  // Long names are truncated so the pill width stays bounded - without it an
  // "Algebra Basics: Variables, Expressions, and Equations" pill could cover
  // two or three neighbours.
  const text = truncateLabel(node.name);
  const w = ctx.measureText(text).width + 8 / k;
  const h = fontSize + 4 / k;
  const y = (node.y ?? 0) + (node.size ?? 4) + 3 / k;
  return { text, box: { x: (node.x ?? 0) - w / 2, y, w, h } };
}

/** The pill, and the name on it - a soft backing so it reads over links. */
function paint(
  ctx: CanvasRenderingContext2D,
  { text, box }: { text: string; box: Box },
  k: number,
  colours: LabelColours
) {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = colours.pill;
  roundedRect(ctx, box, 3 / k);
  ctx.fill();
  ctx.fillStyle = colours.text;
  ctx.fillText(text, box.x + box.w / 2, box.y + 2 / k);
}

/** One node's name, under it. */
export function drawLabel(
  ctx: CanvasRenderingContext2D,
  node: LabelledNode,
  k: number,
  colours: LabelColours
) {
  if (node.x == null || node.y == null) return;
  paint(ctx, pillFor(ctx, node, k), k, colours);
}

const overlaps = (a: Box, b: Box, gap: number) =>
  a.x < b.x + b.w + gap &&
  b.x < a.x + a.w + gap &&
  a.y < b.y + b.h + gap &&
  b.y < a.y + a.h + gap;

/**
 * The resting map's names: `nodes` in priority order, each named unless its
 * pill would touch one already drawn - a zoomed-out map names a city, not
 * every city in a cluster on top of each other. Each pill is also slid
 * sideways to stay inside `[left, right]`, the visible strip of the graph,
 * so a hub near the edge is named rather than cut off.
 */
export function drawRestingLabels(
  ctx: CanvasRenderingContext2D,
  nodes: LabelledNode[],
  k: number,
  {
    colours,
    left,
    right,
  }: { colours: LabelColours; left: number; right: number }
) {
  const placed: Box[] = [];
  const edge = 4 / k;
  for (const node of nodes) {
    if (node.x == null || node.y == null) continue;
    const pill = pillFor(ctx, node, k);
    const { box } = pill;
    box.x = Math.min(Math.max(box.x, left + edge), right - edge - box.w);
    if (placed.some(p => overlaps(p, box, 2 / k))) continue;
    placed.push(box);
    paint(ctx, pill, k, colours);
  }
}

/**
 * A rounded rectangle path, hand-rolled because `ctx.roundRect()` isn't
 * available in every browser we target.
 */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  { x, y, w, h }: Box,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Lesson names above their islands.
 *
 * They used to be pills centred ON the cluster, which is exactly where its
 * most important concepts sit, so the label covered the very dots it named.
 * Now each name sits just above its island's top edge, like a caption over a
 * group, with no pill: a halo in the card colour keeps it legible where a
 * link passes behind it.
 *
 * Biggest cluster first, because a label that would collide with one already
 * placed is dropped, so order is priority. And capped: a real archive has
 * hundreds of lessons, and a reader cannot hold more than about twenty names
 * at once, so the biggest are the ones worth the pixels.
 */
export const MAX_CLUSTER_LABELS = 18;

/** Lesson titles are long; past this they end in an ellipsis. */
export const MAX_CLUSTER_LABEL_CHARS = 28;

/** Cluster label size on screen, in px; bolder than a concept name. */
export const CLUSTER_LABEL_PX = 12;

export const truncateClusterName = (
  name: string,
  max: number = MAX_CLUSTER_LABEL_CHARS
) => (name.length > max ? name.slice(0, max - 1).trimEnd() + '…' : name);

/**
 * Where a cluster's name goes, in graph units: centred on the cluster, its
 * bottom a small gap above the island's top edge (`y - reach`).
 */
export function clusterLabelBox(
  cluster: { x: number; y: number; reach: number },
  textWidth: number,
  k: number
): Box {
  const h = CLUSTER_LABEL_PX / k;
  const gap = 4 / k;
  return {
    x: cluster.x - textWidth / 2,
    y: cluster.y - cluster.reach - gap - h,
    w: textWidth,
    h,
  };
}

export function drawClusterLabels(
  ctx: CanvasRenderingContext2D,
  clusters: ReadonlyArray<{
    subject: string;
    count: number;
    x: number;
    y: number;
    /** The island's radius: the label sits above it. */
    reach: number;
  }>,
  k: number,
  options: {
    colours: { text: string; halo: string };
    left: number;
    right: number;
    inView: (x: number, y: number) => boolean;
    /** 0..1, so the names can fade out as concept names take over. */
    alpha?: number;
  }
): void {
  const marks = clusters
    .filter(c => options.inView(c.x, c.y))
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_CLUSTER_LABELS);
  if (marks.length === 0) return;

  ctx.save();
  ctx.globalAlpha = options.alpha ?? 1;
  ctx.font = `600 ${CLUSTER_LABEL_PX / k}px "Inter Variable", Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3 / k;
  ctx.strokeStyle = options.colours.halo;
  ctx.fillStyle = options.colours.text;

  const placed: Box[] = [];
  const edge = 4 / k;
  for (const c of marks) {
    const text = truncateClusterName(c.subject);
    const box = clusterLabelBox(c, ctx.measureText(text).width, k);
    box.x = Math.min(
      Math.max(box.x, options.left + edge),
      options.right - edge - box.w
    );
    if (placed.some(p => overlaps(p, box, 4 / k))) continue;
    placed.push(box);
    const cx = box.x + box.w / 2;
    ctx.strokeText(text, cx, box.y);
    ctx.fillText(text, cx, box.y);
  }
  ctx.restore();
}
